import { ManufacturingItem } from "../../../utils/hooks/manfacturingDashboard";
import React, { Dispatch, MouseEvent, ReactNode, SetStateAction } from "react";
import { requestingNewTab } from "../../../utils/hooks/links";
import getBoardLink from "../../../utils/links/boards";
import {
  BoardIcon,
  CalendarItemContainer,
  CalendarItemPill,
  CalendarItemText,
  CalendarItemTopRow,
  CustomerSpecification,
  CustomerSpecificationsRow,
  InventoryRow,
  Nickname,
  PILL_COLOR_SCHEMES,
  PillVariant,
  ProjectName,
} from "./DashboardStyles";
import LoadingIcon from "../../common/LoadingIcon";
import {
  LeadTimeDays,
  ManufacturingStepStatus,
  ProductionStatus,
  Shipment,
} from "../../../reducers/uploadedBoard";
import {
  dateStringToDate,
  dateStringToHumanReadableDate,
} from "../../../utils/valueFormatting";
import { ItemStatus } from "./CalendarView";
import { useBoard } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import Icon from "../../common/Icon";
import FlashPCBTooltip from "../../common/FlashPCBTooltip";
import { useShipment } from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";

function getIconSrc(status: ManufacturingStepStatus): string {
  if (status == ManufacturingStepStatus.READY) {
    return "/icons/check_black.svg";
  } else if (status == ManufacturingStepStatus.WAITING) {
    return "/icons/close.svg";
  } else {
    return "/icons/remove_black.svg";
  }
}

function getPillVariant(status: ManufacturingStepStatus): PillVariant {
  if (status == ManufacturingStepStatus.READY) {
    return "ready";
  } else if (status == ManufacturingStepStatus.WAITING) {
    return "notReady";
  } else {
    return "neutral";
  }
}

function CalendarInventoryItem({
  abbreviation,
  status,
  tooltipContent,
}: {
  abbreviation: string;
  status: ManufacturingStepStatus;
  tooltipContent?: ReactNode;
}) {
  const pillVariant = getPillVariant(status);
  const iconSrc = getIconSrc(status);
  return (
    <FlashPCBTooltip content={tooltipContent}>
      <CalendarItemPill colorScheme={PILL_COLOR_SCHEMES[pillVariant]}>
        {abbreviation}:{" "}
        <Icon src={iconSrc} alt={pillVariant} iconDimension={20} />
      </CalendarItemPill>
    </FlashPCBTooltip>
  );
}

function getStencilAndPanelsTooltipMessage(
  item: string,
  status: ManufacturingStepStatus,
): string {
  if (status == ManufacturingStepStatus.WAITING) {
    return `${item} not yet checked in`;
  } else if (status == ManufacturingStepStatus.READY) {
    return `${item} checked in`;
  } else {
    return `${item} not needed`;
  }
}

function StencilStatus({ shipment }: { shipment: Shipment }) {
  const status = shipment.manufacturing_checklist.inventory_status.stencil;
  return (
    <CalendarInventoryItem
      abbreviation={"S"}
      status={status}
      tooltipContent={getStencilAndPanelsTooltipMessage("Stencil", status)}
    />
  );
}

function PanelStatus({ shipment }: { shipment: Shipment }) {
  const status = shipment.manufacturing_checklist.inventory_status.panel;
  return (
    <CalendarInventoryItem
      abbreviation={"P"}
      status={status}
      tooltipContent={getStencilAndPanelsTooltipMessage("Panels", status)}
    />
  );
}

function getComponentsTooltipMessage(
  partsWithAllocationsSatisfyingTheRequiredAmount: number,
  numParts: number,
): string {
  if (numParts == 0) {
    return "This board is blank";
  } else {
    return `${partsWithAllocationsSatisfyingTheRequiredAmount.toLocaleString()} of ${numParts.toLocaleString()} components meeting required amount.`;
  }
}

function getComponentStatus(
  partsWithAllocationsSatisfyingTheRequiredAmount: number,
  numParts: number,
) {
  if (numParts == 0) {
    return ManufacturingStepStatus.NOT_APPLICABLE;
  } else if (partsWithAllocationsSatisfyingTheRequiredAmount >= numParts) {
    return ManufacturingStepStatus.READY;
  } else {
    return ManufacturingStepStatus.WAITING;
  }
}

function ComponentsStatus({ shipment }: { shipment: Shipment }) {
  const partsWithAllocationsSatisfyingTheRequiredAmount =
    shipment.shipment_parts.reduce((numAllocated, shipmentPart) => {
      if (shipmentPart.amount_allocated >= shipmentPart.required_amount) {
        numAllocated += 1;
      }
      return numAllocated;
    }, 0) ?? 0;
  const numParts = shipment.shipment_parts.length;
  const status = getComponentStatus(
    partsWithAllocationsSatisfyingTheRequiredAmount,
    numParts,
  );
  return (
    <CalendarInventoryItem
      abbreviation={"C"}
      status={status}
      tooltipContent={getComponentsTooltipMessage(
        partsWithAllocationsSatisfyingTheRequiredAmount,
        numParts,
      )}
    />
  );
}

export default function CalendarItem({
  manufacturingItem,
  setShipmentToShow,
  onClick,
}: {
  manufacturingItem: ManufacturingItem;
  setShipmentToShow?: Dispatch<SetStateAction<ManufacturingItem | null>>;
  onClick?: (shipmentId: string) => void;
}) {
  const { board } = useBoard(manufacturingItem.board_id);
  const { shipment } = useShipment(
    manufacturingItem.board_id,
    manufacturingItem.shipment_id,
  );

  const handleItemClick = (event: MouseEvent<Element>) => {
    event.preventDefault();
    if (setShipmentToShow === undefined) {
      onClick?.(manufacturingItem.shipment_id);
      return;
    }
    if (requestingNewTab(event)) {
      window.open(
        getBoardLink(
          manufacturingItem.board_id,
          true,
          manufacturingItem.shipment_id,
        ),
        "_blank",
      );
    } else {
      setShipmentToShow(manufacturingItem);
    }
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (board === null || shipment === null) {
    return (
      <CalendarItemContainer status={"loading"}>
        <LoadingIcon centerHorizontally />
      </CalendarItemContainer>
    );
  }

  const getStatus = () => {
    if (shipment.production_status === ProductionStatus.MANUFACTURED) {
      return ItemStatus.SHIPPED;
    } else if (dateStringToDate(manufacturingItem.ship_date) < today) {
      return ItemStatus.LATE;
    } else {
      return ItemStatus.MANUFACTURING;
    }
  };

  const rush = shipment.base_lead_time_days != LeadTimeDays.TEN;

  return (
    <CalendarItemContainer onClick={handleItemClick} status={getStatus()}>
      <CalendarItemTopRow>
        {board.top_preview && (
          <BoardIcon>
            {/* Don't need to worry about SEO here so disable lint */}
            {/* eslint-disable-next-line */}
            <img
              src={board.top_preview}
              alt={`${board.project_name} preview`}
            />
          </BoardIcon>
        )}
        <CalendarItemText>
          <ProjectName>{board.project_name}</ProjectName>
          <Nickname>{board.internal_nickname}</Nickname>
          <div>
            {shipment.num_boards.toLocaleString()} boards,{" "}
            {shipment.shipment_parts.length.toLocaleString()} unique parts
          </div>
          {!setShipmentToShow && (
            <div>{dateStringToHumanReadableDate(shipment.ship_date)}</div>
          )}
        </CalendarItemText>
      </CalendarItemTopRow>
      {rush && (
        <CalendarItemPill colorScheme={PILL_COLOR_SCHEMES.neutral}>
          High priority
        </CalendarItemPill>
      )}
      <InventoryRow>
        <StencilStatus shipment={shipment} />
        <PanelStatus shipment={shipment} />
        <ComponentsStatus shipment={shipment} />
      </InventoryRow>
      <CustomerSpecificationsRow>
        <CustomerSpecification>
          {board.manufacturing_details.customer_specifications.cleaning && (
            <FlashPCBTooltip content={"cleaning requested"}>
              <Icon src={"/icons/water_drop.svg"} alt={"Cleaning"} />
            </FlashPCBTooltip>
          )}
        </CustomerSpecification>
        <CustomerSpecification>
          {board.manufacturing_details.customer_specifications
            .conformal_coating && (
            <FlashPCBTooltip content={"conformal coating requested"}>
              <Icon src={"/icons/spray_can.svg"} alt={"Coating"} />
            </FlashPCBTooltip>
          )}
        </CustomerSpecification>
        <CustomerSpecification>
          {board.manufacturing_details.customer_specifications
            .xray_inspection && (
            <FlashPCBTooltip content={"x-ray inspection requested"}>
              <Icon src={"/icons/radiation.svg"} alt={"Xray"} />
            </FlashPCBTooltip>
          )}
        </CustomerSpecification>
        <CustomerSpecification>
          {board.manufacturing_details.customer_specifications.notes !== "" && (
            <FlashPCBTooltip
              content={`Notes: ${board.manufacturing_details.customer_specifications.notes}`}
            >
              <Icon src={"/icons/attention_black.svg"} alt={"Special"} />
            </FlashPCBTooltip>
          )}
        </CustomerSpecification>
      </CustomerSpecificationsRow>
    </CalendarItemContainer>
  );
}
